import { useQuery } from '@tanstack/react-query';

import { mediaService } from '@/services/media/mediaService';

export interface AssetReuseInfo {
  /** How many `social_posts` currently reference this asset (media_urls or
   * cover_image_url), scoped to the caller's account by RLS
   * (`social_posts_owner_all`: `user_id = current_account_owner_id()`) — no
   * explicit `.eq('user_id', …)` filter needed, since one would only risk
   * ANDing against RLS with the wrong id. See `mediaService.getReferencingPostCount`. */
  postCount: number;
  /** Whether this media row currently carries non-empty `overlay_layers`. */
  hasOverlay: boolean;
}

/**
 * Surfaces the asset-reuse coupling `ImageBody.tsx`'s editor otherwise hides
 * completely: `media.overlay_layers` is mutated on the shared library row,
 * not per-post, so adding text to a reused asset silently bakes that text
 * into every post referencing it at publish time (44% of post-referenced
 * media URLs are used in >1 post, one in 24 posts, as of 2026-08-15 — no
 * warning existed anywhere in the UI before this).
 *
 * Deliberately thin: both underlying calls already exist and are already
 * correctly identity-scoped (RLS-enforced) and format-agnostic for the
 * media.url relative-vs-full split (`getReferencingPostCount` resolves via
 * the row's own `storage_bucket` column before querying `social_posts`) —
 * this hook does not re-implement that matching, it composes it.
 */
export function useAssetReuseInfo(mediaId: string | undefined) {
  return useQuery<AssetReuseInfo>({
    queryKey: ['asset-reuse-info', mediaId],
    queryFn: async () => {
      if (!mediaId) return { postCount: 0, hasOverlay: false };
      const [postCount, layers] = await Promise.all([
        mediaService.getReferencingPostCount(mediaId),
        // Fails open (unlike its throwing default) — this is ambient context,
        // not a blocking read. A transient overlay-lookup failure should
        // still let the reuse COUNT render rather than losing both.
        mediaService.getOverlayLayers(mediaId).catch(() => []),
      ]);
      return { postCount, hasOverlay: layers.length > 0 };
    },
    enabled: !!mediaId,
    staleTime: 30_000,
  });
}
